import { useParams, Link, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { EAR_EXCEPTION_GROUPS, buildEarExceptionIndex } from "@/data/earExceptions";

// eCFR builds a stable URL for any § inside title 15. For sub-paragraphs
// (e.g. § 740.9(a)(1)) the anchor is "#p-740.9(a)(1)". We derive both from
// the item's CFR citation.
function ecfrUrlForCfr(cfr) {
  const m = cfr.match(/15 CFR § (\d+)\.(\d+)(\([^)]+\))?(\([^)]+\))?/);
  if (!m) return "https://www.ecfr.gov/current/title-15/subtitle-B/chapter-VII/subchapter-C/part-740";
  const [, part, section, p1 = "", p2 = ""] = m;
  const anchor = `p-${part}.${section}${p1}${p2}`;
  return `https://www.ecfr.gov/current/title-15/subtitle-B/chapter-VII/subchapter-C/part-${part}/section-${part}.${section}#${anchor}`;
}

// Placeholder detail page — the compliance checklist body will land here in
// a follow-up build. For now, we show the code, name, CFR citation, plain-
// English description, a breadcrumb back to the index (and to the parent
// section for nested exceptions), and a "checklist coming soon" callout.
export default function EARExceptionDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const index = useMemo(() => buildEarExceptionIndex(), []);
  const item = index[slug];

  if (!item) {
    return (
      <Layout>
        <SEO title="EAR License Exception Not Found" path={`/tools/ear-license-exceptions/${slug}`} />
        <section className="max-w-4xl mx-auto px-6 lg:px-8 py-24">
          <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-4">Not found</p>
          <h1 className="font-serif text-3xl text-ink mb-6">We couldn&rsquo;t find that License Exception.</h1>
          <button
            onClick={() => navigate("/tools/ear-license-exceptions")}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brass-deep hover:text-brass"
            data-testid="ear-exception-detail-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the EAR Exceptions index
          </button>
        </section>
      </Layout>
    );
  }

  const isSub = !!item.parent;
  const ecfrUrl = ecfrUrlForCfr(item.cfr);

  return (
    <Layout>
      <SEO
        title={`${item.code} — ${item.name}`}
        description={item.description}
        path={`/tools/ear-license-exceptions/${item.slug}`}
      />

      {/* Breadcrumb */}
      <section className="border-b border-hairline" data-testid="ear-exception-detail-breadcrumb">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-6 pb-3 text-[11px] uppercase tracking-[0.22em] text-ink-soft flex items-center gap-2 flex-wrap">
          <Link to="/tools/ear-license-exceptions" className="hover:text-brass">EAR Exceptions</Link>
          {isSub && (
            <>
              <span aria-hidden>›</span>
              <Link to={`/tools/ear-license-exceptions/${item.parent.slug}`} className="hover:text-brass">
                {item.parent.code}
              </Link>
            </>
          )}
          <span aria-hidden>›</span>
          <span className="text-ink">{item.code}</span>
        </div>
      </section>

      {/* Hero */}
      <section className="border-b border-hairline">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-10 pb-14">
          <Eyebrow>{item.group.label}</Eyebrow>
          <p className="font-mono text-[13px] font-semibold text-brass-deep mt-6">{item.cfr}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.05] tracking-tight mt-3">
            {item.name}
          </h1>
          <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed">
            {item.description}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={ecfrUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-ink hover:text-ivory transition-colors"
              data-testid="ear-exception-detail-ecfr-link"
            >
              Read on eCFR <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
            </a>
            <Link
              to="/tools/license-check"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-brass-deep hover:text-brass"
              data-testid="ear-exception-detail-checker-link"
            >
              Run the EAR License Checker →
            </Link>
          </div>
        </div>
      </section>

      {/* Nested sub-exceptions (if this IS the parent) */}
      {item.subs && (
        <section className="border-b border-hairline bg-ivory-alt" data-testid="ear-exception-detail-subs">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
            <Eyebrow>Sub-exceptions within {item.code}</Eyebrow>
            <p className="text-ink-soft italic font-serif text-lg leading-snug mt-4 mb-8">
              {item.code} houses {item.subs.length} distinct exceptions. Pick the one that fits the transaction.
            </p>
            <ul className="border-t border-hairline">
              {item.subs.map((sub) => (
                <li key={sub.slug} className="border-b border-hairline">
                  <Link
                    to={`/tools/ear-license-exceptions/${sub.slug}`}
                    className="group grid grid-cols-12 gap-4 py-5 hover:bg-ivory transition-colors -mx-3 px-3"
                    data-testid={`ear-exception-detail-sub-${sub.slug}`}
                  >
                    <span className="col-span-4 sm:col-span-3 font-mono text-[13px] font-semibold text-brass-deep">
                      {sub.cfr.replace("15 CFR § ", "§ ")}
                    </span>
                    <span className="col-span-8 sm:col-span-9 text-ink group-hover:text-brass transition-colors leading-snug">
                      {sub.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Checklist placeholder */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-16" data-testid="ear-exception-detail-checklist-placeholder">
        <Eyebrow>Compliance Checklist</Eyebrow>
        <h2 className="font-serif text-3xl text-ink mt-4 mb-6">Coming soon.</h2>
        <p className="text-ink-soft leading-relaxed max-w-2xl">
          A step-by-step eligibility and recordkeeping checklist for {item.code} is being drafted. When published, it will walk through every threshold question — item scope, destination, end-user, prior authorization, reporting, and return conditions — with links to the underlying regulation.
        </p>
      </section>

      {/* Related — other items in the same group */}
      <RelatedEarExceptions currentSlug={item.slug} groupId={item.group.id} />
    </Layout>
  );
}

function RelatedEarExceptions({ currentSlug, groupId }) {
  const group = EAR_EXCEPTION_GROUPS.find((g) => g.id === groupId);
  if (!group) return null;
  const others = group.items.filter((i) => i.slug !== currentSlug).slice(0, 6);
  if (others.length === 0) return null;
  return (
    <section className="border-t border-hairline bg-ivory-alt">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14">
        <Eyebrow>Also in {group.label}</Eyebrow>
        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 border-t border-hairline">
          {others.map((o) => (
            <li key={o.slug} className="border-b border-hairline-soft">
              <Link
                to={`/tools/ear-license-exceptions/${o.slug}`}
                className="group flex items-baseline gap-3 py-3 hover:text-brass transition-colors"
                data-testid={`ear-exception-detail-related-${o.slug}`}
              >
                <span className="font-mono text-[12px] font-semibold text-brass-deep shrink-0 w-14">{o.code}</span>
                <span className="text-[14px] text-ink group-hover:text-brass leading-snug">{o.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
